import type {
  UiTestRunAllCompletePayload,
  UiTestRunCompletePayload,
  UiTestScenarioId,
} from '@/modules/ui-test/uiTest.types'

export const UI_TEST_RUN_QUERY = 'uiTestRun' as const
export const UI_TEST_SHOW_RESULT_QUERY = 'uiTestShowResult' as const
export const UI_TEST_SHOW_RESULT_ALL_QUERY = 'uiTestShowResultAll' as const
export const UI_TEST_RESULT_STORAGE_KEY = 'azl_ui_test_last_result' as const
export const UI_TEST_RUN_ALL_RESULT_STORAGE_KEY = 'azl_ui_test_run_all_result' as const
const UI_TEST_RETURN_PATH_KEY = 'azl_ui_test_return_path' as const
const UI_TEST_RUN_ALL_QUEUE_KEY = 'azl_ui_test_run_all_queue' as const
const UI_TEST_RUN_ALL_RESULTS_KEY = 'azl_ui_test_run_all_results' as const
const UI_TEST_RUN_ALL_META_KEY = 'azl_ui_test_run_all_meta' as const

export type UiTestRunAllProgress = {
  current: number
  total: number
}

export function readUiTestResult(): UiTestRunCompletePayload | null {
  try {
    const raw = sessionStorage.getItem(UI_TEST_RESULT_STORAGE_KEY)
    if (!raw) return null
    sessionStorage.removeItem(UI_TEST_RESULT_STORAGE_KEY)
    return JSON.parse(raw) as UiTestRunCompletePayload
  } catch {
    return null
  }
}

export function writeUiTestResult(payload: UiTestRunCompletePayload): void {
  sessionStorage.setItem(UI_TEST_RESULT_STORAGE_KEY, JSON.stringify(payload))
}

/** Trang catalog sẽ nhận kết quả sau khi chạy xong. Mặc định là DEV TEST. */
export function setUiTestReturnPath(path: string): void {
  sessionStorage.setItem(UI_TEST_RETURN_PATH_KEY, path)
}

export function readUiTestReturnPath(fallback: string): string {
  const raw = sessionStorage.getItem(UI_TEST_RETURN_PATH_KEY)
  return raw && raw.startsWith('/') ? raw : fallback
}

export function clearUiTestReturnPath(): void {
  sessionStorage.removeItem(UI_TEST_RETURN_PATH_KEY)
}

export function readUiTestRunAllResult(): UiTestRunAllCompletePayload | null {
  try {
    const raw = sessionStorage.getItem(UI_TEST_RUN_ALL_RESULT_STORAGE_KEY)
    if (!raw) return null
    sessionStorage.removeItem(UI_TEST_RUN_ALL_RESULT_STORAGE_KEY)
    return JSON.parse(raw) as UiTestRunAllCompletePayload
  } catch {
    return null
  }
}

export function writeUiTestRunAllResult(payload: UiTestRunAllCompletePayload): void {
  sessionStorage.setItem(UI_TEST_RUN_ALL_RESULT_STORAGE_KEY, JSON.stringify(payload))
}

export function getUiTestRunAllProgress(): UiTestRunAllProgress | null {
  try {
    const raw = sessionStorage.getItem(UI_TEST_RUN_ALL_META_KEY)
    if (!raw) return null
    return JSON.parse(raw) as UiTestRunAllProgress
  } catch {
    return null
  }
}

export function isUiTestRunAllActive(): boolean {
  return getUiTestRunAllProgress() != null
}

/** Bắt đầu chuỗi chạy tất cả — trả về scenario đầu tiên cần navigate. */
export function beginUiTestRunAll(ids: UiTestScenarioId[]): UiTestScenarioId | null {
  if (ids.length === 0) return null
  const [first, ...rest] = ids
  sessionStorage.setItem(UI_TEST_RUN_ALL_QUEUE_KEY, JSON.stringify(rest))
  sessionStorage.setItem(UI_TEST_RUN_ALL_RESULTS_KEY, JSON.stringify([]))
  sessionStorage.setItem(
    UI_TEST_RUN_ALL_META_KEY,
    JSON.stringify({ current: 1, total: ids.length } satisfies UiTestRunAllProgress),
  )
  return first
}

function readRunAllResults(): UiTestRunCompletePayload[] {
  try {
    const raw = sessionStorage.getItem(UI_TEST_RUN_ALL_RESULTS_KEY)
    return raw ? (JSON.parse(raw) as UiTestRunCompletePayload[]) : []
  } catch {
    return []
  }
}

function buildRunAllBatch(results: UiTestRunCompletePayload[]): UiTestRunAllCompletePayload {
  let passed = 0
  let skipped = 0
  let failed = 0
  let scenarioFailed = 0

  for (const r of results) {
    passed += r.summary.passed
    skipped += r.summary.skipped
    failed += r.summary.failed
    if (!r.summary.ok) scenarioFailed += 1
  }

  const scenarios = results.length
  const ok = scenarioFailed === 0

  return {
    results,
    aggregate: {
      scenarios,
      passed,
      skipped,
      failed,
      ok,
      message: ok
        ? `Hoàn tất ${scenarios} kịch bản — tất cả đạt`
        : `Hoàn tất ${scenarios} kịch bản — ${scenarioFailed} kịch bản có lỗi`,
    },
  }
}

function clearUiTestRunAllSession(): void {
  sessionStorage.removeItem(UI_TEST_RUN_ALL_QUEUE_KEY)
  sessionStorage.removeItem(UI_TEST_RUN_ALL_RESULTS_KEY)
  sessionStorage.removeItem(UI_TEST_RUN_ALL_META_KEY)
}

/**
 * Sau mỗi kịch bản trong chuỗi Run ALL — lưu kết quả, trả về id tiếp theo hoặc null nếu xong.
 */
export function advanceUiTestRunAll(payload: UiTestRunCompletePayload): UiTestScenarioId | null {
  const results = [...readRunAllResults(), payload]
  sessionStorage.setItem(UI_TEST_RUN_ALL_RESULTS_KEY, JSON.stringify(results))

  let queue: UiTestScenarioId[] = []
  try {
    const raw = sessionStorage.getItem(UI_TEST_RUN_ALL_QUEUE_KEY)
    queue = raw ? (JSON.parse(raw) as UiTestScenarioId[]) : []
  } catch {
    queue = []
  }

  const [next, ...rest] = queue
  sessionStorage.setItem(UI_TEST_RUN_ALL_QUEUE_KEY, JSON.stringify(rest))

  const meta = getUiTestRunAllProgress()
  if (meta && next) {
    sessionStorage.setItem(
      UI_TEST_RUN_ALL_META_KEY,
      JSON.stringify({ current: meta.current + 1, total: meta.total }),
    )
  }

  return next ?? null
}

/** Kết thúc chuỗi Run ALL — gom batch và dọn session. */
export function finalizeUiTestRunAll(): UiTestRunAllCompletePayload {
  const batch = buildRunAllBatch(readRunAllResults())
  clearUiTestRunAllSession()
  return batch
}
